import type { GoalCategory } from './events.js';

/**
 * FLAVOUR — the register the app speaks in.
 *
 * A fifth tunable, and the narrowest one on purpose:
 *
 *   features.ts  — what is SHOWN
 *   ruleset.ts   — how behaviour SCORES
 *   tiers.ts     — who may USE it
 *   selectors.ts — the shape the UI READS
 *   flavour.ts   — what things are CALLED                        (this file)
 *
 * It changes names and nothing else. No rank threshold moves, no XP award
 * changes, no capability appears or disappears. That constraint is the whole
 * design: flavour is derived from a question answered in twenty seconds by
 * someone who has never used the app, which makes it the weakest signal in
 * the product — far too weak to decide what anyone is allowed to see. Letting
 * it pick words is safe; letting it pick features would mean a student who
 * tapped the wrong chip on day one never finds the thing they'd have loved.
 *
 * Two registers, not seven. "Grandmaster" and "National Topper" are good
 * names for a seventeen-year-old sitting HSC and slightly insulting to a
 * thirty-year-old training for a marathon — and "Novice" is worse. The split
 * that matters is exam-culture versus everything else; inventing a separate
 * vocabulary per category would be six sets of names nobody asked for.
 */
export type Flavour = 'student' | 'general';

export const flavours: readonly Flavour[] = ['student', 'general'];

/**
 * What the student is preparing for.
 *
 * Asked once, in onboarding, and it earns the interruption by paying twice:
 * it picks the register AND it is the key the goal preset packs are filed
 * under, so answering "HSC" is what eventually puts the Physics chapter list
 * one tap away. A question that only set an adjective would not be worth
 * asking.
 */
export type StudentPath = 'hsc' | 'ssc' | 'admission' | 'university' | 'other';

export const studentPaths: readonly StudentPath[] = [
  'hsc',
  'ssc',
  'admission',
  'university',
  'other',
];

/** Exam candidates get the exam-culture names. Everyone else does not. */
export function flavourForPath(path: StudentPath): Flavour {
  return path === 'other' ? 'general' : 'student';
}

/**
 * The fallback before the question has been answered.
 *
 * Someone who skipped onboarding but set an academic goal has told us what
 * they are through their behaviour, which is better evidence than a chip
 * anyway. Only an explicitly non-academic goal moves them off the default —
 * this app is for students, and that is the right guess when guessing.
 */
export function flavourForCategory(category: GoalCategory): Flavour {
  return category === 'academic' ? 'student' : 'general';
}

/**
 * Resolve the register from what is known, most-explicit first.
 *
 * `path` is an answer the student gave about themselves; a category is a
 * property of one goal they happen to be running. When both exist the answer
 * wins, and when neither does the app stays what it was built for.
 */
export function flavourFor(input: {
  path?: StudentPath | undefined;
  category?: GoalCategory | undefined;
}): Flavour {
  if (input.path !== undefined) return flavourForPath(input.path);
  if (input.category !== undefined) return flavourForCategory(input.category);
  return 'student';
}
